import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { AlertTriangle, CheckCircle, Loader2 } from "lucide-react"
import {
  AuthField,
  AuthLayout,
  AlgoBuilderLogo,
  Button,
  PasswordInput,
} from "@/components"
import { confirmPasswordReset, validateResetToken } from "../system/api"

export default function ResetPassword() {
  const { token } = useParams()
  const navigate = useNavigate()

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(true)
  const [message, setMessage] = useState(null)
  const [isValid, setIsValid] = useState(false)
  const [tokenData, setTokenData] = useState(null)
  const [passwordError, setPasswordError] = useState("")
  const [confirmError, setConfirmError] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)

  useEffect(() => {
    const validate = async () => {
      setIsValidating(true)
      try {
        const response = await validateResetToken(token)
        if (response.valid) {
          setIsValid(true)
          setTokenData(response)
        } else {
          setIsValid(false)
          setMessage("This password reset link is invalid or has expired.")
        }
      } catch (error) {
        setIsValid(false)
        if (error.errorCode === "RESET_TOKEN_INVALID") {
          setMessage(
            "This password reset link is invalid or has expired. Please request a new password reset link.",
          )
        } else {
          setMessage(
            error.errorMessage ||
              "This password reset link is invalid or has expired.",
          )
        }
      } finally {
        setIsValidating(false)
      }
    }
    validate()
  }, [token])

  const validatePassword = () => {
    let ok = true
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long")
      ok = false
    } else {
      setPasswordError("")
    }
    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match")
      ok = false
    } else {
      setConfirmError("")
    }
    return ok
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isLoading) return

    if (!validatePassword()) return

    setIsLoading(true)
    setMessage(null)

    try {
      await confirmPasswordReset(token, password)
      setIsSuccess(true)
    } catch (error) {
      if (error.errorCode === "RESET_TOKEN_INVALID") {
        setMessage(
          "This password reset link has expired or already been used. Please request a new password reset link.",
        )
      } else {
        setMessage(
          error.errorMessage || "An error occurred. Please try again later.",
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  const brandSlot = (
    <a href="https://algobuilder.com">
      <AlgoBuilderLogo height={22} />
    </a>
  )
  const footerSlot = (
    <>&copy; {new Date().getFullYear()} AlgoBuilder. All rights reserved.</>
  )

  if (isValidating) {
    return (
      <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
        <div className="text-center py-4">
          <Loader2
            size={36}
            strokeWidth={1.2}
            className="text-primary mx-auto mb-3 animate-spin"
          />
          <p className="text-body text-muted-foreground">
            Validating reset link...
          </p>
        </div>
      </AuthLayout>
    )
  }

  if (!isValid) {
    return (
      <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
        <div className="text-center py-4">
          <AlertTriangle
            size={36}
            strokeWidth={1.2}
            className="text-destructive mx-auto mb-3"
          />
          <h1 className="text-page-title text-foreground mb-1.5">
            Invalid Reset Link
          </h1>
          <p className="text-body text-muted-foreground leading-relaxed max-w-[280px] mx-auto mb-4">
            {message}
          </p>
          <Button
            className="w-full text-label"
            onClick={() => navigate("/sign/in")}
          >
            Back to Login
          </Button>
        </div>
      </AuthLayout>
    )
  }

  if (isSuccess) {
    return (
      <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
        <div className="text-center py-4">
          <CheckCircle
            size={36}
            strokeWidth={1.2}
            className="text-primary mx-auto mb-3"
          />
          <h1 className="text-page-title text-foreground mb-1.5">
            Password reset
          </h1>
          <p className="text-body text-muted-foreground leading-relaxed max-w-[280px] mx-auto mb-4">
            Your password has been reset. You can now sign in with your new
            password.
          </p>
          <Button
            className="w-full text-label"
            onClick={() => navigate("/sign/in")}
          >
            Go to Sign In
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
      <div className="flex flex-col">
        <div className="text-center mb-5">
          <h1 className="text-page-title text-foreground mb-1">
            Reset your password
          </h1>
          <p className="text-body text-muted-foreground">
            {tokenData?.name
              ? `Hi ${tokenData.name}, enter your new password below.`
              : "Enter your new password below."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <AuthField label="New password" error={passwordError}>
            <PasswordInput
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                if (passwordError) setPasswordError("")
                if (message) setMessage(null)
              }}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              autoFocus
              minLength={8}
              disabled={isLoading}
              className="h-9 text-body bg-background border-border"
            />
          </AuthField>

          <AuthField label="Confirm password" error={confirmError}>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
                if (confirmError) setConfirmError("")
                if (message) setMessage(null)
              }}
              placeholder="Re-enter your password"
              autoComplete="new-password"
              minLength={8}
              disabled={isLoading}
              className="h-9 text-body bg-background border-border"
            />
          </AuthField>

          <p className="text-caption text-muted-foreground -mt-1">
            Use at least 8 characters. Mix letters, numbers, and symbols for
            stronger security.
          </p>

          {message && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-caption text-destructive">
              {message}
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full text-label"
          >
            {isLoading && (
              <Loader2 size={14} className="animate-spin mr-1.5" />
            )}
            {isLoading ? "Resetting..." : "Reset password"}
          </Button>
        </form>

        <p className="text-center text-body text-muted-foreground mt-5">
          <Link
            to="/sign/in"
            className="text-primary hover:text-primary/80 no-underline font-semibold transition-colors"
          >
            Back to Login
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}
